import "design-system/styles/index.scss";

import { LazyComponentsProvider } from "design-system/components/system/lazy-component/use-lazy-components";
import { TranslationsProvider } from "design-system/hooks/use-translations";
import { StoreProvider } from "design-system/store/store-provider";
import { frontendComponents } from "frontend/components";
import { ContentfulErrorPageProps } from "frontend/contentful/components/pages/error-page.contentful";
import { getLocaleRedirect } from "frontend/contentful/components/utils/get-locale-redirect";
import {
  removePermissionedEntries,
  resolveAudienceAccessValues,
  userAccess,
} from "frontend/contentful/components/utils/get-user-access";
import { RenderContentfulEntry } from "frontend/contentful/components/utils/render-contentful-entry";
import { getPageProps } from "frontend/contentful/lib/get-page-props";
import { getContentfulRedirect } from "frontend/contentful/lib/get-redirect";
import {
  ContentfulLivePreview,
  useContentful,
} from "frontend/hooks/use-contentful";
import { PagePropsProvider, usePageProps } from "frontend/hooks/use-page-props";
import { checkSiteVisibility } from "frontend/lib/check-site-visibility";
import { createServerContext } from "frontend/lib/create-server-context";
import { handleFacultyRISPage } from "frontend/lib/handle-faculty-ris-page";
import { logEvent } from "frontend/lib/log-event";
import { PageProps } from "frontend/types";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { PropsWithChildren, useEffect } from "react";

const ErrorPage = dynamic<ContentfulErrorPageProps>(() =>
  import("frontend/contentful/components/pages/error-page.contentful").then(
    (m) => ({ default: m.ContentfulErrorPage }),
  ),
);

// Faculty RIS pages pull in a large component graph (detail page, toppers,
// article footer, tease feed, profile tabs). Load them lazily so they stay out
// of the universal [[...slug]] first-load bundle and only ship on faculty URLs.
const FacultyProfilePageRenderer = dynamic(() =>
  import("frontend/contentful/components/pages/faculty-profile-page").then(
    (m) => ({ default: m.FacultyProfilePageRenderer }),
  ),
);

const FacultyItemPageRenderer = dynamic(() =>
  import("frontend/contentful/components/pages/faculty-item-page").then(
    (m) => ({ default: m.FacultyItemPageRenderer }),
  ),
);

const FacultyResearchPageRenderer = dynamic(() =>
  import("frontend/contentful/components/pages/faculty-research-page").then(
    (m) => ({ default: m.FacultyResearchPageRenderer }),
  ),
);

const FacultyTeachingPageRenderer = dynamic(() =>
  import("frontend/contentful/components/pages/faculty-teaching-page").then(
    (m) => ({ default: m.FacultyTeachingPageRenderer }),
  ),
);

function RenderFacultyPage() {
  const { facultyData } = usePageProps();

  if (facultyData?.pageVariant === "profile-detail") {
    return <FacultyProfilePageRenderer />;
  }
  if (facultyData?.pageVariant === "item-detail") {
    return <FacultyItemPageRenderer />;
  }
  if (facultyData?.pageVariant === "research-detail") {
    return <FacultyResearchPageRenderer />;
  }
  if (facultyData?.pageVariant === "teaching-detail") {
    return <FacultyTeachingPageRenderer />;
  }

  return null;
}

function RenderPage() {
  const { pageType } = usePageProps();

  if (pageType === "faculty") {
    return <RenderFacultyPage />;
  }

  return <RenderContentfulPage />;
}

function RenderContentfulPage() {
  const { entry, status, includes, contentful } = usePageProps();
  const { useContentfulLiveUpdates } = useContentful();
  const liveEntry = useContentfulLiveUpdates(entry);

  if (includes.Entry.length > 2500 && contentful.isPreview) {
    return (
      <ErrorPage
        override={{
          pageTitle: "Error",
          title: "Error!",
          body: (
            <>
              <h4>Please contact IT.</h4>
              <p>
                Page is too big ({includes.Entry.length} entries), possible
                circular reference.
              </p>
            </>
          ),
        }}
      />
    );
  }

  if (!liveEntry || status === 404) {
    return <ErrorPage />;
  }

  return <RenderContentfulEntry entry={liveEntry} />;
}

function WrappedTranslationsProvider({
  locale,
  children,
}: PropsWithChildren<{
  locale: string;
}>) {
  return (
    <TranslationsProvider locale={locale}>{children}</TranslationsProvider>
  );
}

export default function Page(props: PageProps) {
  useEffect(() => {
    // set props on window for debugging
    window.props = props;
  }, [props]);

  return (
    <StoreProvider location={props.request} framework={{ Head }}>
      <LazyComponentsProvider components={frontendComponents}>
        <PagePropsProvider {...props}>
          <WrappedTranslationsProvider locale={props?.locale}>
            <ContentfulLivePreview isPreview={props?.contentful?.isPreview}>
              <RenderPage />
            </ContentfulLivePreview>
          </WrappedTranslationsProvider>
        </PagePropsProvider>
      </LazyComponentsProvider>
    </StoreProvider>
  );
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  req,
  res,
  resolvedUrl,
  locale,
  defaultLocale,
  locales,
}) => {
  const ctx = createServerContext({
    slug: resolvedUrl,
    headers: req.headers,
    locale: locale,
    defaultLocale: defaultLocale,
    supportedLocales: locales,
  });

  const url = `${ctx.request.domain}.hbs.edu${ctx.request.path}${ctx.request.search}`;
  const timing = Date.now();

  // Faculty RIS pages (not Contentful) — routed and gated by
  // handleFacultyRISPage, which returns null (falls through) unless the faculty
  // Site is set Live in Contentful, keeping prod dark until then.
  if (ctx.request.path.startsWith("/faculty/")) {
    const result = await handleFacultyRISPage(ctx, req, resolvedUrl, timing);
    if (result) {
      if ("redirect" in result) {
        return result;
      }

      if (!ctx.contentful.isPreview && result.statusCode !== 500) {
        res.setHeader(
          "cache-control",
          "public, s-maxage=300, stale-if-error=7200",
        );
      }

      res.statusCode = result.statusCode;
      return { props: result.props };
    }
    // Fall through to Contentful for other /faculty/* pages
  }

  const { pageProps } = await getPageProps({ ctx });

  if (!pageProps.entry || !pageProps.site) {
    const redirect = await getContentfulRedirect(ctx);

    if (redirect) {
      console.info(`Redirect from ${url} to ${redirect.destination}`);
      return { redirect };
    }

    pageProps.status = 404;
  }

  if (pageProps.status === 404) {
    res.statusCode = 404;
  }

  // Set status code to 500 if there are any errors so that the CDN can use a backup version of the page
  if (ctx.errors.length > 0) {
    res.statusCode = 500;
  }

  // allow setting a failure rate for testing
  if (ctx.request.headers["x-failure-rate"]) {
    const rate = parseFloat(ctx.request.headers["x-failure-rate"] as string);
    if (Math.random() < rate / 100) {
      res.statusCode = 500;
    }
  }

  // Check if user is logged in and has the correct permissions by using a separate function
  const redirect = await userAccess(ctx, resolvedUrl, pageProps);

  if (redirect?.destination) {
    return { redirect };
  } else if (redirect?.props) {
    pageProps.session = redirect.props.session;
  }

  if (pageProps.session) {
    const { userCustomGroups, userAppRoles } =
      await resolveAudienceAccessValues(pageProps, ctx);
    pageProps.session.user.customGroups = userCustomGroups;
    pageProps.session.user.appRoles = userAppRoles;

    await removePermissionedEntries(pageProps, ctx, pageProps.session.user);
  }

  // Check if the site is live or if the user is previewing the site
  const siteRedirect = checkSiteVisibility(pageProps, ctx);
  if (siteRedirect) return { redirect: siteRedirect };

  // Check if current request is for a locale that doesn't have translations
  const localeRedirect = getLocaleRedirect(ctx, resolvedUrl, pageProps);
  if (localeRedirect?.destination) {
    return { redirect: localeRedirect };
  }

  logEvent({
    event: "pageview",
    ms: Date.now() - timing,
    status: res.statusCode,
    url: req.url || "",
  });

  if (
    !ctx.contentful.isPreview &&
    !pageProps.session?.user.loggedIn &&
    res.statusCode !== 500
  ) {
    // cache anonymous and non-preview content for 5 min
    res.setHeader("cache-control", "public, s-maxage=300, stale-if-error=7200");
  }

  // set a short living pull_request cookie it is seen in the query string
  if (ctx.request.query["pull_request"]) {
    // sanitize, only digits
    const pullRequest = ctx.request.query["pull_request"].replace(/[^\d]/g, "");
    res.appendHeader(
      "set-cookie",
      `pull_request=${pullRequest}; Path=/; Max-Age=120; HttpOnly; Secure; SameSite=Strict`,
    );
  }

  // for security, remove cookies from the request object
  // this is to prevent any sensitive HTTP only cookies from being exposed
  pageProps.request.cookies = {};

  return { props: pageProps };
};
